import { useState } from "react";

import type { Purpose, Version, VersionState } from "../../api/types";
import {
  formatBytes,
  formatCount,
  formatDateTime,
  joinWords,
  UNKNOWN,
} from "../../lib/format";
import { PURPOSE_LABELS } from "../../lib/metrics";
import { EmptyBlock } from "../ui/StateBlock";
import { AccessDialog } from "./AccessDialog";

/** One plain sentence per state. Progress is a message, never a sixth box. */
const STATE_LABELS: Record<VersionState, string> = {
  awaiting_bytes: "waiting for the file",
  verifying: "reading the file",
  available: "available",
  rejected: "rejected",
  deleted: "deleted",
};

const STATE_TONES: Record<VersionState, string> = {
  awaiting_bytes: "pending",
  verifying: "pending",
  available: "ok",
  rejected: "danger",
  deleted: "neutral",
};

/**
 * What a version's state means, in the words a GTM person needs.
 *
 * `rejected` is the one that carries the server's own reason: the console has
 * three of them and each says something different about what to do next. A
 * reason that fell into the wrong sentence would tell somebody a refused file
 * was merely unreadable, so each is named, not defaulted (ADR-0018).
 */
function stateSentence(version: Version): string {
  switch (version.state) {
    case "awaiting_bytes":
      return "The record exists, the bytes have not arrived yet. Your browser is sending them.";
    case "verifying":
      return "The bytes arrived. The console is computing the checksum and reading the header, the column types and the row count, in one pass.";
    case "available":
      return "The console has read these exact bytes where they now live. Everything on this card, it measured.";
    case "rejected":
      switch (version.rejected_reason) {
        case "upload_never_arrived":
          return "The file never arrived inside its window. Nothing was kept — start another version and send it again.";
        case "malware_found":
          return "The scanner refused this file, so the console kept nothing. The dataset's trace names what it found.";
        default:
          return "The console could not read this file, so it kept nothing. A file it cannot describe is not a dataset.";
      }
    case "deleted":
      return "The bytes are gone, on purpose, with a receipt. The record is not.";
  }
}

/** The second copy, said only when it was actually looked for and found. */
function replicaSentence(version: Version): string {
  if (version.replicated_at === null) {
    return (
      version.replication_reason ??
      "No second copy has been confirmed for this file yet."
    );
  }
  if (version.replica_check === "size_only") {
    return `A copy of the same size is confirmed in the other region — ${formatDateTime(version.replicated_at)}. It was not re-read, so this says the size matches and nothing more.`;
  }
  return `A second copy is confirmed in the other region, with the same checksum — ${formatDateTime(version.replicated_at)}.`;
}

interface VersionCardProps {
  version: Version;
  datasetId: string;
  /** The declared target column of the dataset, for the declared-vs-measured line. */
  declaredTargetColumn: string | null;
  /** What the current consent covers. */
  permitted: Purpose[];
}

/** One version: what was declared, what was measured, and what is missing. */
function VersionCard({
  version,
  datasetId,
  declaredTargetColumn,
  permitted,
}: VersionCardProps): JSX.Element {
  const [asking, setAsking] = useState(false);

  return (
    <article className="version" data-tone={STATE_TONES[version.state]}>
      <header className="version__head">
        <h3 className="version__title">
          Version {version.number}
          <span className="version__file mono">{version.filename}</span>
        </h3>
        <span className="badge" data-tone={STATE_TONES[version.state]}>
          {STATE_LABELS[version.state]}
        </span>
      </header>

      <p className="version__state">{stateSentence(version)}</p>

      <dl className="facts">
        <div className="facts__item">
          <dt>Size</dt>
          <dd>{formatBytes(version.size_bytes)}</dd>
        </div>
        <div className="facts__item">
          <dt>Rows</dt>
          <dd>
            {version.row_count === null ? (
              <span className="facts__absent">
                {UNKNOWN}
                {version.row_count_reason ? ` — ${version.row_count_reason}` : ""}
              </span>
            ) : (
              formatCount(version.row_count)
            )}
          </dd>
        </div>
        <div className="facts__item">
          <dt>Columns</dt>
          <dd>{formatCount(version.column_count)}</dd>
        </div>
        <div className="facts__item">
          <dt>Checksum</dt>
          <dd className="mono facts__checksum">
            {version.checksum_sha256 ?? UNKNOWN}
          </dd>
        </div>
      </dl>

      {declaredTargetColumn ? (
        <p className="version__line">
          {version.target_column_present === null ? (
            <>
              The declared target column{" "}
              <span className="mono">{declaredTargetColumn}</span> has not been
              checked against this file yet.
            </>
          ) : version.target_column_present ? (
            <>
              The declared target column{" "}
              <span className="mono">{declaredTargetColumn}</span> is in this
              file.
            </>
          ) : (
            <>
              The declared target column{" "}
              <span className="mono">{declaredTargetColumn}</span> was not found
              in this file. Correct the declaration, or upload the file that has
              it. Nothing was rejected for it.
            </>
          )}
        </p>
      ) : null}

      <p className="version__line version__line--quiet">
        {replicaSentence(version)}
      </p>

      {/*
        The scan, in the server's own sentence (ADR-0018). Performed, the
        reason names the engine and its signature date — never "clean", which
        would be a verdict about the future. Not performed, the client half
        says so plainly and the server's reason says why: no scanner, stored
        before there was one, or still on its way through the check. A screen
        that implied a check we did not perform would be worse than the
        missing check.
      */}
      {version.scan.performed ? (
        // A malware-refused card already carries the verdict in its state
        // sentence above; printing the scan line too would say the same fact
        // twice in two near-identical sentences.
        version.scan.reason && version.rejected_reason !== "malware_found" ? (
          <p className="version__line version__line--quiet">
            {version.scan.reason}
          </p>
        ) : null
      ) : (
        <p className="version__line version__line--quiet">
          Not scanned for malware.{" "}
          {version.scan.reason ?? "No scanner is configured on this console."}
        </p>
      )}

      <p className="version__line version__line--quiet">
        {version.attribution}
      </p>

      {version.consent_at_upload ? (
        <p className="version__line version__line--quiet">
          When these bytes arrived, the client allowed{" "}
          {joinWords(
            version.consent_at_upload.permissions.map(
              (purpose) => PURPOSE_LABELS[purpose].toLowerCase(),
            ),
          )}{" "}
          — {version.consent_at_upload.evidence}. What may be done with it today
          is the consent above, not this one.
          {/*
            The frozen consent's reference as an address, when it is one — the
            server derived it under the ledger's rule (ADR-0017). Fixed words,
            never the address; the console claims nothing about what is behind
            the door.
          */}
          {version.consent_at_upload.evidence_url ? (
            <>
              {" "}
              <a
                href={version.consent_at_upload.evidence_url}
                rel="noreferrer noopener"
                target="_blank"
              >
                open the document
              </a>
            </>
          ) : null}
        </p>
      ) : null}

      {version.schema && version.schema.length > 0 ? (
        <details className="version__schema">
          <summary>The columns the console read</summary>
          <p className="form__note">
            Names, types and how many values are missing. Never a value and
            never a row — that is the client's data, and it is not browsed in a
            browser tab.
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Column</th>
                <th scope="col">Type</th>
                <th scope="col">Missing</th>
              </tr>
            </thead>
            <tbody>
              {version.schema.map((column) => (
                <tr key={column.name}>
                  <td className="mono">{column.name}</td>
                  <td className="mono">{column.type}</td>
                  <td>{formatCount(column.null_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}

      {version.state === "available" ? (
        <div className="version__actions">
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setAsking(true)}
          >
            Get this file
          </button>
        </div>
      ) : null}

      {asking ? (
        <AccessDialog
          datasetId={datasetId}
          versionNumber={version.number}
          filename={version.filename}
          permitted={permitted}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </article>
  );
}

interface VersionListProps {
  versions: Version[];
  datasetId: string;
  declaredTargetColumn: string | null;
  permitted: Purpose[];
}

/**
 * Every version of a dataset, newest first.
 *
 * The bytes of a version never change: a client who resends a fixed file is a
 * new version, and the old one still answers "what did that 0.847 come from".
 *
 * Args:
 *     versions: The versions as served.
 *     datasetId: The dataset they belong to.
 *     declaredTargetColumn: What the dataset declares, for the measured line.
 *     permitted: What the current consent covers.
 *
 * Returns:
 *     The list, or the honest empty state.
 */
export function VersionList({
  versions,
  datasetId,
  declaredTargetColumn,
  permitted,
}: VersionListProps): JSX.Element {
  if (versions.length === 0) {
    return (
      <EmptyBlock label="No file has been added to this dataset yet." />
    );
  }
  return (
    <div className="versions">
      {versions.map((version) => (
        <VersionCard
          key={version.number}
          version={version}
          datasetId={datasetId}
          declaredTargetColumn={declaredTargetColumn}
          permitted={permitted}
        />
      ))}
    </div>
  );
}
